import React, { useState } from "react";
import { BrainCircuit, CreditCard } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { API_BASE_URL } from "../config";

const PERSIAN_DIGITS = "۰۱۲۳۴۵۶۷۸۹";

function toPersianNum(n: number | string): string {
    return String(n).replace(/[0-9]/g, (d) => PERSIAN_DIGITS[parseInt(d, 10)]);
}

function formatNumberFa(n: number): string {
    const s = Math.round(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, "٬");
    return toPersianNum(s);
}

interface PlanFromAPI {
    id: number | string;
    name: string;
    display_name: string;
    description?: string;
    price_monthly: number;
    features?: unknown;
    is_default?: boolean;
    is_active?: boolean;
}

interface PaymentsPageProps {
    plan?: PlanFromAPI;
    onSuccess?: () => void;
}

// ایجاد axios instance اختصاصی برای پرداخت (با هدر خودکار)
const paymentAxios = axios.create({
    baseURL: API_BASE_URL,
});

// اضافه کردن توکن به هدر در هر درخواست (مثل interceptor)
paymentAxios.interceptors.request.use((config) => {
    const token = localStorage.getItem("auth_token");
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// مدیریت خطای 401 (مثل interceptor)
paymentAxios.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            localStorage.removeItem("token");
            window.location.href = "/login"; // یا navigate("/login", { replace: true });
        }
        return Promise.reject(error);
    }
);

export const PaymentsPage: React.FC<PaymentsPageProps> = ({ onSuccess }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const plan = (location.state as { plan: PlanFromAPI } | null)?.plan;

    if (!plan) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-gray-900">
                <div className="text-center">
                    <h2 className="text-xl font-bold text-red-600 dark:text-red-400">پلنی انتخاب نشده است</h2>
                    <p className="text-gray-600 dark:text-gray-400 mt-2">لطفاً از صفحه انتخاب پلن اقدام کنید</p>
                </div>
            </div>
        );
    }

    const PLAN_PRICE = plan.price_monthly;

    // حذف محاسبات ارزش افزوده و استفاده از قیمت پایه
    const totalPriceWithTax = PLAN_PRICE;

    const formattedOriginalPrice = formatNumberFa(PLAN_PRICE);
    const formattedTotal = formatNumberFa(totalPriceWithTax);

    const [nationalCode, setNationalCode] = useState<string>("");
    const isValidCode = nationalCode.length === 10 && /^\d{10}$/.test(nationalCode);
    const [isProcessing, setIsProcessing] = useState<boolean>(false);

    const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value
            .replace(/[۰-۹]/g, (d) => String.fromCharCode("۰".charCodeAt(0) + "۰۱۲۳۴۵۶۷۸۹".indexOf(d)))
            .replace(/[a-zA-Z]/g, "");
        if (val.length <= 10) setNationalCode(val);
    };

    const handlePayment = async () => {
        if (!isValidCode || isProcessing) return;

        setIsProcessing(true);

        try {
            const response = await paymentAxios.post("/api/v1/payments/init", {
                amount: totalPriceWithTax,
                plan_id: plan.id.toString(),
                national_code: nationalCode,
            });

            const { redirect_url } = response.data;

            window.location.href = redirect_url;
        } catch (error: any) {
            if (error.response?.status !== 401) {
                alert("خطا در انجام پرداخت. لطفا دوباره امتحان کنید.");
            }
            setIsProcessing(false);
        }
    };


    return (
        <div
            className="min-h-screen bg-gradient-to-br from-gray-50 via-gray-100 to-gray-200 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center p-4 sm:p-6 lg:p-8">
            <div
                className="w-full max-w-md bg-white dark:bg-gray-900 rounded-3xl shadow-[0_25px_55px_-15px_rgba(0,0,0,0.2)] border border-gray-200 dark:border-gray-700 overflow-hidden animate-fade-in-up">
                <div
                    className="pt-8 pb-4 px-6 text-center bg-[#f3f4f6] dark:bg-[#1f2937] border-b border-gray-200 dark:border-gray-700">
                    <BrainCircuit size={48} className="mx-auto mb-2 text-gray-600 dark:text-gray-300" />
                    <h2 className="font-bold text-xl tracking-tight text-gray-900 dark:text-white">صفحه پرداخت</h2>
                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                        <strong>{plan.display_name}</strong>
                    </p>
                </div>

                <div className="px-6 pt-4 pb-6">
                    <div className="space-y-5">
                        <div className="flex justify-between items-center text-sm">
                            <span className="text-gray-600 dark:text-gray-300">قیمت:</span>
                            <span
                                className="font-semibold text-gray-900 dark:text-white">{formattedOriginalPrice} تومان</span>
                        </div>

                        {/* بخش ارزش افزوده حذف شد */}

                        <div
                            className="flex justify-between items-center border-t border-gray-200 dark:border-gray-700 pt-4">
                            <span
                                className="text-sm font-medium text-gray-600 dark:text-gray-300">مبلغ قابل پرداخت:</span>
                            <span
                                className="text-lg font-bold text-gray-900 dark:text-white">{formattedTotal} تومان</span>
                        </div>
                    </div>
                </div>

                <div className="px-6 pb-4">
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        کد ملی (۱۰ رقم)
                    </label>
                    <input
                        type="text"
                        inputMode="numeric"
                        value={nationalCode}
                        onChange={handleCodeChange}
                        className="w-full px-4 py-2 rounded-xl border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#a7a79f] dark:focus:ring-[#5a5a54] focus:border-transparent transition-all outline-none"
                    />
                    {nationalCode.length > 0 && (
                        <p
                            className={`mt-1 text-xs ${
                                isValidCode
                                    ? "text-green-600 dark:text-green-400"
                                    : "text-red-500 dark:text-red-400"
                            }`}
                        >
                            {isValidCode
                                ? "کد ملی صحیح است"
                                : "کد ملی باید صحیح وارد شود."}
                        </p>
                    )}
                </div>

                <div className="border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex flex-col gap-3">
                    <button
                        onClick={handlePayment}
                        disabled={isProcessing || !isValidCode}
                        className={`w-full py-3 px-4 rounded-xl font-semibold transition-all flex items-center justify-center gap-2
    ${
                            isProcessing
                                ? "bg-[#3a3a36] dark:bg-[#deddd6] text-white dark:text-[#242421] cursor-wait"
                                : isValidCode
                                    ? "bg-[#3a3a36] hover:bg-[#242421] dark:bg-[#deddd6] dark:hover:bg-white text-white dark:text-[#242421] shadow-md hover:shadow-lg cursor-pointer"
                                    : "bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                        }`}
                    >
                        {isProcessing ? (
                            <>
                                <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none"
                                     viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor"
                                            strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor"
                                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                در حال پرداخت...
                            </>
                        ) : (
                            <>
                                <CreditCard size={18}/>
                                پرداخت آنی
                            </>
                        )}
                    </button>

                </div>
                {/* نماد اعتماد الکترونیکی */}
                <div className="flex justify-center pb-5">
                    <a referrerPolicy="origin" target="_blank"
                       href="https://trustseal.enamad.ir/?id=717876&Code=w6W2odl1vCsPCzyFEJCj6HNOdkk6WD8b">
                        <img
                            referrerPolicy="origin"
                            src="https://trustseal.enamad.ir/logo.aspx?id=717876&Code=w6W2odl1vCsPCzyFEJCj6HNOdkk6WD8b"
                            alt="نماد اعتماد الکترونیکی"
                            style={{cursor: "pointer"}}
                            data-code="w6W2odl1vCsPCzyFEJCj6HNOdkk6WD8b"
                        />
                    </a>
                </div>

            </div>
        </div>
    );
};
